import type { ReactNode } from 'react';
import type {
  CompanyProfileDetails,
  JobSeekerProfileDetails,
  TwinHardSkill,
  TwinProfileDetails as TwinProfileDetailsType,
  TwinProject,
} from '../../types';

function ProfileField({ label, value }: { label: string; value?: string | null }) {
  if (!value || !value.trim()) return null;
  return (
    <div className="bg-[#1c1c2a] rounded-lg px-3 py-2">
      <p className="text-[10px] text-[#64748b] uppercase tracking-wide">{label}</p>
      <p className="text-xs text-[#f1f5f9] mt-0.5 leading-relaxed whitespace-pre-wrap break-words">{value}</p>
    </div>
  );
}

function SkillChips({ skills }: { skills: TwinHardSkill[] }) {
  if (!skills.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {skills.map((s, i) => (
        <span
          key={`${s.name}-${i}`}
          title={s.description || s.importance || undefined}
          className="inline-flex items-center gap-1 rounded-md bg-[#1a2235] px-2 py-1 text-[11px] text-[#94a3b8]"
        >
          <span className="text-[#8fb4ff] font-medium">{s.name}</span>
          {s.years ? <span className="text-[#64748b]">· {s.years} yrs</span> : null}
        </span>
      ))}
    </div>
  );
}

function ProjectsList({ projects }: { projects: TwinProject[] }) {
  if (!projects.length) return null;
  return (
    <div className="space-y-2">
      {projects.map((p, i) => (
        <div key={`${p.name}-${i}`} className="bg-[#1c1c2a] rounded-lg px-3 py-2">
          <p className="text-xs font-medium text-[#f1f5f9]">{p.name}</p>
          <p className="text-xs text-[#94a3b8] mt-0.5 leading-relaxed whitespace-pre-wrap break-words">
            {p.description}
          </p>
          {(p.hard_part || p.expectation) && (
            <p className="text-[10px] text-[#64748b] mt-1">
              {p.hard_part ? `Hard part: ${p.hard_part}` : `Expectation: ${p.expectation}`}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-[10px] text-[#64748b] uppercase tracking-wide mb-1.5">{label}</p>
      {children}
    </div>
  );
}

function CompanyProfile({ details }: { details: CompanyProfileDetails }) {
  const location = [details.location_type, details.office_city, details.location_address]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">Hiring Profile</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <ProfileField label="Domain" value={details.domain_label || details.domain} />
        <ProfileField label="Title" value={details.job_title_label || details.job_title} />
        <ProfileField label="Location" value={location} />
        <ProfileField label="Experience Level" value={details.experience_level} />
        <ProfileField label="Employment Type" value={details.employment_type} />
        <ProfileField label="Salary Range" value={details.salary_range} />
        <ProfileField label="Timeline" value={details.urgency} />
        <ProfileField label="Benefits" value={details.benefits} />
        <ProfileField label="Industry Experience" value={details.specific_experience} />
      </div>

      {details.hard_skills.length > 0 && (
        <Section label="Required Skills">
          <SkillChips skills={details.hard_skills} />
        </Section>
      )}
      {details.soft_skills.length > 0 && (
        <Section label="Preferred Soft Skills">
          <SkillChips skills={details.soft_skills} />
        </Section>
      )}
      {details.projects.length > 0 && (
        <Section label="Preferred Projects / Portfolio">
          <ProjectsList projects={details.projects} />
        </Section>
      )}
      {details.screening.length > 0 && (
        <Section label="Screening Questions">
          <div className="space-y-2">
            {details.screening.map((s, i) => (
              <div key={i} className="bg-[#1c1c2a] rounded-lg px-3 py-2">
                <p className="text-xs text-[#f1f5f9]">{s.question}</p>
                <p className="text-[10px] text-[#64748b] mt-1">Look for: {s.criteria}</p>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

function JobSeekerProfile({ details }: { details: JobSeekerProfileDetails }) {
  const location =
    details.location_type === 'Remote'
      ? 'Remote'
      : [details.location_type, details.location_pref].filter(Boolean).join(' in ');
  const topPriority = details.priorities[0];

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">Candidate Profile</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <ProfileField label="Domain" value={topPriority?.domain_label || topPriority?.domain} />
        <ProfileField label="Title" value={topPriority?.job_title_label || topPriority?.job_title} />
        <ProfileField label="Preferred Location" value={location} />
        <ProfileField label="Experience Level" value={details.experience_level} />
        <ProfileField label="Employment Type" value={details.employment_type} />
        <ProfileField label="Availability" value={details.availability} />
        <ProfileField label="Desired Salary" value={details.desired_salary} />
        <ProfileField label="Industry Experience" value={details.industry_experience} />
      </div>

      {details.priorities.length > 1 && (
        <Section label="All Target Roles (ranked)">
          <div className="flex flex-wrap gap-1.5">
            {details.priorities.map((p, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 rounded-md bg-[#1a2235] px-2 py-1 text-[11px] text-[#94a3b8]"
              >
                #{p.rank} {p.job_title_label || p.job_title}
              </span>
            ))}
          </div>
        </Section>
      )}
      {details.hard_skills.length > 0 && (
        <Section label="Hard Skills">
          <SkillChips skills={details.hard_skills} />
        </Section>
      )}
      {details.soft_skills.length > 0 && (
        <Section label="Soft Skills">
          <SkillChips skills={details.soft_skills} />
        </Section>
      )}
      {details.projects.length > 0 && (
        <Section label="Projects">
          <ProjectsList projects={details.projects} />
        </Section>
      )}
      {details.work_experience.length > 0 && (
        <Section label="Work Experience">
          <div className="space-y-2">
            {details.work_experience.map((w, i) => (
              <div key={i} className="bg-[#1c1c2a] rounded-lg px-3 py-2">
                <p className="text-xs font-medium text-[#f1f5f9]">
                  {w.company}
                  {w.designation ? ` · ${w.designation}` : ''}
                  {w.years ? ` (${w.years} yrs)` : ''}
                </p>
                <p className="text-xs text-[#94a3b8] mt-0.5">{w.role}</p>
                {w.hard_part && <p className="text-[10px] text-[#64748b] mt-1">Hard part: {w.hard_part}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      <ProfileField label="Bio Pitch" value={details.bio_pitch} />
      <ProfileField label="Key Achievements" value={details.achievements} />
    </div>
  );
}

interface TwinProfileDetailsProps {
  type: 'company' | 'job_seeker' | 'unknown';
  details: TwinProfileDetailsType | null | undefined;
}

/** Hard skills, domain/title selected, preferred location, projects - reconstructed
 * from the twin's stored context/skills text blobs by the backend. */
export function TwinProfileDetails({ type, details }: TwinProfileDetailsProps) {
  if (!details) return null;

  return type === 'company' ? (
    <CompanyProfile details={details as CompanyProfileDetails} />
  ) : (
    <JobSeekerProfile details={details as JobSeekerProfileDetails} />
  );
}
